import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { setPropertyId } from '@/lib/api-client';
import { API_BASE_URL, ENV } from '@/lib/config';

interface AuthUser {
  id: string;
  email: string;
  name: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  propertyId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// TODO: Replace with real auth (WorkOS, custom JWT, etc.)
// In local dev, auto-login via org-management's dev-token endpoint.
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthContextValue>({
    user: null,
    propertyId: null,
    isAuthenticated: false,
    isLoading: true,
    token: null,
  });

  useEffect(() => {
    const isLocal = ENV === 'local';
    if (!isLocal) {
      setState(s => ({ ...s, isLoading: false }));
      return;
    }

    // Fetch a dev token from org-management (no auth required)
    fetch(`${API_BASE_URL}/api/auth/dev-auto-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
      .then(res => res.json())
      .then(data => {
        const pid = data.property_id ?? null;
        setPropertyId(pid);
        setState({
          user: { id: data.user?.id ?? 'dev', email: 'dev@localhost', name: 'Dev User' },
          propertyId: pid,
          isAuthenticated: true,
          isLoading: false,
          token: data.token ?? null,
        });
      })
      .catch(() => {
        setState(s => ({ ...s, isLoading: false }));
      });
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
